import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceReviewer } from '@shared/api/contracts/compliance';
import type { ReasonFactorValue } from '@shared/ui';
import { ReasonFactorDialog, StatusBanner, Switch, TypographyMuted } from '@shared/ui';

interface ReviewersPanelProps {
  items: ComplianceReviewer[];
  userId: number | null;
  loading?: boolean;
  onToggle: (userId: number, enabled: boolean, value: ReasonFactorValue) => Promise<boolean>;
}

export function ReviewersPanel({ items, userId, loading, onToggle }: ReviewersPanelProps) {
  const { t } = useTranslation('cabinet');
  const [target, setTarget] = useState<{ id: number; enabled: boolean } | null>(null);
  const reviewers = items.filter((item) => item.complianceReviewer).length;

  return (
    <div className="surface-card space-y-4">
      {reviewers < 2 ? <StatusBanner>{t('compliance.reviewers.needTwo')}</StatusBanner> : null}
      {!items.length ? <TypographyMuted>{t('compliance.reviewers.empty')}</TypographyMuted> : null}
      <ul className="divide-y divide-border">
        {items.map((item) => {
          const self = userId !== null && item.id === userId;
          return (
            <li key={item.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{item.email}</p>
                {item.bannedAt ? <p className="text-xs text-muted-foreground">{t('compliance.reviewers.banned')}</p> : null}
                {self ? <p className="text-xs text-muted-foreground">{t('compliance.reviewers.self')}</p> : null}
              </div>
              <Switch
                checked={item.complianceReviewer}
                disabled={self || Boolean(item.bannedAt) || loading}
                aria-label={item.email}
                onCheckedChange={(checked) => setTarget({ id: item.id, enabled: checked === true })}
              />
            </li>
          );
        })}
      </ul>
      <ReasonFactorDialog
        open={Boolean(target)}
        title={t(target?.enabled ? 'compliance.reviewers.grantTitle' : 'compliance.reviewers.revokeTitle')}
        description={t('compliance.reviewers.description')}
        confirmLabel={t('compliance.reviewers.save')}
        loading={loading}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
        onConfirm={(value) => {
          if (!target) return;
          void onToggle(target.id, target.enabled, value).then((ok) => {
            if (ok) setTarget(null);
          });
        }}
      />
    </div>
  );
}
